import { supabase } from '../lib/supabase';
import { sendNewsletterConfirmation } from './emailNotificationService';

export const emailTemplateService = {
  async getAllActive() {
    try {
      // Assuming a boolean field or we just return all for now to avoid breaking if schema lacks it
      const { data, error } = await supabase.from('email_templates').select('*').order('name');
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('emailTemplateService.getAllActive error:', e);
      return [];
    }
  },
  async getAll() {
    try {
      const { data, error } = await supabase.from('email_templates').select('*').order('name');
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('emailTemplateService.getAll error:', e);
      return [];
    }
  },
  async create(templateData) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from('email_templates').insert([{
      name: templateData.name,
      subject: templateData.subject,
      html_content: templateData.html_content || templateData.htmlContent,
      created_by: user?.id
    }]).select();
    if (error) throw error;
    return data ? data[0] : null;
  },
  async update(id, templateData) {
    const { data, error } = await supabase.from('email_templates').update({
      name: templateData.name,
      subject: templateData.subject,
      html_content: templateData.html_content || templateData.htmlContent,
      is_active: templateData.is_active
    }).eq('id', id).select();
    if (error) throw error;
    return data ? data[0] : null;
  },
  async delete(id) {
    const { error } = await supabase.from('email_templates').delete().eq('id', id);
    if (error) throw error;
  }
};

export const topicService = {
  async getAllTopics() {
    try {
      const { data, error } = await supabase.from('newsletter_topics').select('*').order('name');
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('topicService.getAllTopics error:', e);
      return [];
    }
  },
  async createTopic(topicData) {
    const { data, error } = await supabase.from('newsletter_topics').insert(topicData).select();
    if (error) throw error;
    return data ? data[0] : null;
  },
  async updateTopic(id, topicData) {
    const { data, error } = await supabase.from('newsletter_topics').update(topicData).eq('id', id).select();
    if (error) throw error;
    return data ? data[0] : null;
  },
  async deleteTopic(id) {
    const { error } = await supabase.from('newsletter_topics').delete().eq('id', id);
    if (error) throw error;
  }
};

export const newsletterService = {
  async getAllTopics() {
    return topicService.getAllTopics();
  },
  async getNewsletterSubscriptions(userId) {
    try {
      const { data: subscriber } = await supabase.from('newsletter_subscribers').select('id').eq('user_id', userId).maybeSingle();
      if (!subscriber) return [];
      
      const { data, error } = await supabase.from('subscriber_newsletter_subscriptions').select('*').eq('subscriber_id', subscriber.id);
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('newsletterService.getNewsletterSubscriptions error:', e);
      return [];
    }
  },
  async updateNewsletterSubscriptions(userId, subscriptions) {
     // Stub out for now, to prevent UI crashes if called
     console.log('Update subscriptions stub', userId, subscriptions);
     return true;
  },
  async getAllSubscribers(filters = {}) {
    try {
      let query = supabase.from('newsletter_subscribers').select('*');
      if (filters.isActive !== undefined) {
        query = query.eq('is_active', filters.isActive);
      }
      const { data, error } = await query.order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('newsletterService.getAllSubscribers error:', e);
      return [];
    }
  },
  async getAllCampaigns() {
    try {
      const { data, error } = await supabase.from('newsletter_campaigns').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('newsletterService.getAllCampaigns error:', e);
      return [];
    }
  },
  async createCampaign(campaignData) {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase.from('newsletter_campaigns').insert([{
      title: campaignData.title,
      content: campaignData.content,
      type: campaignData.type || 'email',
      status: campaignData.status || 'Planning',
      content_types: campaignData.content_types || [],
      created_by: user?.id
    }]).select();
    if (error) throw error;
    return data ? data[0] : null;
  },
  async updateCampaign(id, campaignData) {
    const { data, error } = await supabase.from('newsletter_campaigns').update({
      title: campaignData.title,
      content: campaignData.content,
      type: campaignData.type,
      status: campaignData.status,
      content_types: campaignData.content_types
    }).eq('id', id).select();
    if (error) throw error;
    return data ? data[0] : null;
  },
  async deleteCampaign(id) {
    const { error } = await supabase.from('newsletter_campaigns').delete().eq('id', id);
    if (error) throw error;
  },
  async getContentPreferences(subscriberId) {
    try {
      const { data, error } = await supabase.from('subscriber_topics').select('*, newsletter_topics!inner(name)').eq('subscriber_id', subscriberId);
      if (error) throw error;
      return data || [];
    } catch (e) {
      console.error('newsletterService.getContentPreferences error:', e);
      return [];
    }
  },
  async updateContentPreferences(subscriberId, preferences) {
    const updates = preferences.map(p => ({
        subscriber_id: subscriberId,
        topic_id: p.topic_id,
        is_enabled: p.is_enabled,
    }));
    const { data, error } = await supabase.from('subscriber_topics').upsert(updates, { onConflict: 'subscriber_id, topic_id' });
    if (error) throw error;
    return data;
  },
  async subscribe(email, phoneNumber = null, userId = null) {
    try {
      const { data, error } = await supabase
        .from('newsletter_subscribers')
        .insert({
          email: email,
          phone_number: phoneNumber,
          user_id: userId,
          email_opt_in: true,
          is_active: true
        })
        .select()
        .maybeSingle();

      let resultData = data;

      if (error) {
        if (error.code === '23505') {
          const { data: reactivated, error: updateErr } = await supabase
            .from('newsletter_subscribers')
            .update({ is_active: true, email_opt_in: true })
            .eq('email', email)
            .select()
            .maybeSingle();
          if (updateErr) throw updateErr;
          resultData = reactivated;
        } else {
          throw error;
        }
      }

      // Symmetrically send confirmation email after successful subscription!
      try {
        await sendNewsletterConfirmation(email, null);
      } catch (emailErr) {
        console.error('Failed to send newsletter confirmation email:', emailErr);
      }

      return resultData;
    } catch (e) {
      console.error('newsletterService.subscribe error:', e);
      throw e;
    }
  }
};
